import { parseProblem } from '@reos/api-client';

/** Zrcadlo `ValidationIssue` z `@reos/schemas` (web-admin ten balik v zavislostech nema, viz properties.ts). */
export interface ValidationIssue {
  readonly path: string;
  readonly code: string;
}

/**
 * Chyba z domenoveho API (problem+json). `message` je uz lokalizovany text
 * ze serveru (`problem.title`/`detail`, cs zdrojovy jazyk - CLAUDE.md §5) -
 * volajici ho smi zobrazit primo, nesmi nad nim znovu hardcodovat vlastni text.
 * `issues` (krok UXP2 P2-4) nese strojove cesty poli (`problem.issues`),
 * aby formular mohl zvyraznit konkretni pole, ne jen hodit obecnou hlasku.
 */
export class DomainApiError extends Error {
  readonly status: number;
  readonly type: string | undefined;
  readonly issues: readonly ValidationIssue[];
  /**
   * Krok NOC1 bod 2: `Retry-After` z odpovedi (sekundy). Jen tak muze
   * opakovani cteni (`retryOnRateLimit`) pockat PRESNE tak dlouho, jak si rekl
   * server — bez teto hodnoty by si kazdy volajici hadal vlastni pauzu.
   * `undefined` = hlavicka nebyla, nebo nebyla cislo.
   */
  readonly retryAfterSeconds: number | undefined;

  constructor(
    message: string,
    status: number,
    type?: string,
    issues?: readonly ValidationIssue[],
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'DomainApiError';
    this.status = status;
    this.type = type;
    this.issues = issues ?? [];
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Strojovy kod chyby z `type` problem+json — JEDNO MISTO pro cely admin
 * (krok E2EFIX3).
 *
 * Na drate je `type` URL (`https://docs.reos.local/errors/demo/blocked`,
 * `apps/api/src/lib/problems.ts`), v rucnich atrapach testu casto jen kod.
 * Porovnani na holy kod proto na SKUTECNE odpovedi nikdy nesedelo: zakaz
 * v ukazce se poznal v testu (atrapa `type: 'demo/blocked'`), ale v prohlizeci
 * ne — a makler videl technicky `detail` „demo: blocked operation '…'".
 *
 * Tentyz prevod potrebuje i `authErrorFrom` v `api.ts` (chyby z `/api/auth/*`),
 * proto je tady a ne jako privatni funkce jedne cesty.
 */
export function problemCodeOf(type: string | undefined): string | undefined {
  if (type === undefined) return undefined;
  const at = type.indexOf('/errors/');
  return at === -1 ? type : type.slice(at + '/errors/'.length);
}

/** `Retry-After: 30` → 30; datumovy tvar ani nesmysl se nebere (NOC1 bod 2). */
export function retryAfterSecondsOf(response: Response): number | undefined {
  const raw = response.headers.get('retry-after');
  if (raw === null) return undefined;
  const seconds = Number(raw.trim());
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/** Rozbali odpoved openapi-fetch klienta (`{data, error, response}`) - vyhodi DomainApiError na non-2xx. */
export async function unwrap<T>(
  call: Promise<{ data?: T | undefined; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response.ok) {
    // openapi-fetch uz telo odpovedi precetl do `error` — druhe cteni tehoz
    // Response by skoncilo „Body has already been read", takze ho pouzijeme
    // a `parseProblem` je jen zaloha pro klienty, kteri `error` nedodaji.
    const parsed = parseProblemLike(error) ?? (await parseProblem(response).catch(() => null));
    // 8. 10. 2026 (nalez e2e S6b): u zakazu v ukazce je `detail` technicka
    // stopa („demo: blocked operation 'payment'"), pro cloveka je `title`.
    const proCloveka =
      problemCodeOf(parsed?.type) === 'demo/blocked'
        ? (parsed?.title ?? parsed?.detail)
        : (parsed?.detail ?? parsed?.title);
    throw new DomainApiError(
      proCloveka ?? `HTTP ${response.status}`,
      response.status,
      parsed?.type,
      parsed?.issues,
      retryAfterSecondsOf(response),
    );
  }
  return data as T;
}

/** problem+json uz rozbalene klientem (openapi-fetch `error`). */
function parseProblemLike(
  error: unknown,
): { title?: string; detail?: string; type?: string; issues?: readonly ValidationIssue[] } | null {
  if (error === null || typeof error !== 'object') return null;
  const candidate = error as { title?: unknown; detail?: unknown; type?: unknown; issues?: unknown };
  return {
    ...(typeof candidate.title === 'string' ? { title: candidate.title } : {}),
    ...(typeof candidate.detail === 'string' ? { detail: candidate.detail } : {}),
    ...(typeof candidate.type === 'string' ? { type: candidate.type } : {}),
    ...(Array.isArray(candidate.issues) ? { issues: candidate.issues as readonly ValidationIssue[] } : {}),
  };
}
